import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { LogoMark } from "../components/Logo";
import { toast } from "sonner";
import { Upload, FileText, Image as ImageIcon, Trash2, Download, LogOut, UserCircle, Building2, Receipt, CalendarRange, Plus, LayoutDashboard, Clock, Sparkles, Wallet, QrCode, FileSignature, CheckCircle2, MessageCircle, Calendar } from "lucide-react";
import TrasfertaWidget from "../components/TrasfertaWidget";
import EmployeeCheckinTab from "../components/EmployeeCheckinTab";
import Avatar from "../components/Avatar";
import AvatarPicker from "../components/AvatarPicker";
import ChatModule from "../components/ChatModule";
import WorkModeBadge from "../components/WorkModeBadge";
import AttendanceCalendar from "../components/AttendanceCalendar";

const CATEGORIES = [
  { value: "document", label: "Documento" },
  { value: "photo", label: "Foto" },
  { value: "id_card", label: "Documento identità" },
  { value: "contract", label: "Contratto" },
  { value: "other", label: "Altro" },
];
const LEAVE_TYPES = [
  { value: "holiday", label: "Ferie" },
  { value: "permission", label: "Permesso" },
  { value: "rol", label: "ROL" },
  { value: "law_104", label: "Legge 104" },
  { value: "parental_leave", label: "Congedo parentale" },
  { value: "medical_visit", label: "Visita medica" },
  { value: "union", label: "Sindacale" },
  { value: "illness", label: "Malattia" },
  { value: "other", label: "Altro" },
];
const EXPENSE_CATEGORIES = ["Trasporti", "Pasti", "Alloggio", "Carburante", "Cancelleria", "Altro"];

const ALLOWED = ["image/jpeg", "image/jpg", "image/png", "image/webp", "application/pdf"];
const MAX_MB = 10;

const fileIcon = (mime) => mime?.startsWith("image/")
  ? <ImageIcon className="w-5 h-5 text-[#FF7A29]"/>
  : <FileText className="w-5 h-5 text-[#FF7A29]"/>;

const prettySize = (s) => !s ? "" : s < 1024 ? `${s} B` : s < 1024*1024 ? `${(s/1024).toFixed(1)} KB` : `${(s/(1024*1024)).toFixed(2)} MB`;

const statusBadge = (s) => {
  const cfg = {
    pending: { bg: "bg-amber-100", fg: "text-amber-700", label: "In attesa" },
    approved: { bg: "bg-green-100", fg: "text-green-700", label: "Approvata" },
    rejected: { bg: "bg-red-100", fg: "text-red-700", label: "Rifiutata" },
  }[s] || { bg: "bg-gray-100", fg: "text-gray-600", label: s };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-widest ${cfg.bg} ${cfg.fg}`}>{cfg.label}</span>;
};

const typeLabel = (t) => LEAVE_TYPES.find((x) => x.value === t)?.label || t;

export default function EmployeePortalPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [profile, setProfile] = useState(null);
  const [dashboard, setDashboard] = useState(null);

  // Files
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("document");
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [myAvatar, setMyAvatar] = useState(user?.avatar || "initials");
  const [notes, setNotes] = useState("");

  // Payslips
  const [payslips, setPayslips] = useState([]);

  // Leaves
  const [leaves, setLeaves] = useState([]);
  const [leaveForm, setLeaveForm] = useState({ type: "holiday", start_date: "", end_date: "", hours: "", notes: "" });

  // Expenses
  const [expenses, setExpenses] = useState([]);
  const [expForm, setExpForm] = useState({ title: "", amount: "", category: "Trasporti", date: new Date().toISOString().slice(0,10), notes: "", receipt_base64: "" });
  const [expSubmitting, setExpSubmitting] = useState(false);

  // Signature requests
  const [signRequests, setSignRequests] = useState([]);

  // Chat unread badge
  const [chatUnread, setChatUnread] = useState(0);

  useEffect(() => {
    if (!user) return;
    // Ammetti employee e supervisor (i supervisor sono anche dipendenti — hanno employee_id)
    const allowed = user.role === "employee" || (user.role === "supervisor" && user.employee_id);
    if (!allowed) { nav("/app"); return; }
    api.get("/employee/me").then((r) => setProfile(r.data)).catch(() => {});
    loadDashboard(); loadFiles(); loadPayslips(); loadLeaves(); loadExpenses(); loadSignRequests();
    loadChatUnread();
    const ct = setInterval(loadChatUnread, 30000);
    return () => clearInterval(ct);
  }, [user, nav]); // eslint-disable-line

  const loadChatUnread = async () => { try { setChatUnread(((await api.get("/chat/unread")).data || {}).total || 0); } catch {} };

  const loadDashboard = async () => { try { setDashboard((await api.get("/employee/dashboard")).data); } catch {} };
  const loadFiles = async () => { try { setFiles((await api.get("/employee/files")).data); } catch {} };
  const loadExpenses = async () => { try { setExpenses((await api.get("/employee/expenses")).data); } catch {} };
  const loadPayslips = async () => { try { setPayslips((await api.get("/employee/payslips")).data); } catch {} };
  const loadLeaves = async () => { try { setLeaves((await api.get("/employee/leaves")).data); } catch {} };
  const loadSignRequests = async () => { try { setSignRequests((await api.get("/signature-requests/mine")).data || []); } catch {} };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ALLOWED.includes(file.type)) { toast.error("Formato non consentito (solo JPG, PNG, WEBP, PDF)"); return; }
    if (file.size > MAX_MB * 1024 * 1024) { toast.error(`Max ${MAX_MB}MB`); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setUploading(true);
      try {
        await api.post("/employee/files", {
          filename: file.name, mime_type: file.type,
          content_base64: reader.result, category, notes,
        });
        toast.success("File caricato"); setNotes(""); loadFiles();
      } catch (err) { toast.error(err?.response?.data?.detail || "Errore upload"); }
      finally { setUploading(false); }
    };
    reader.readAsDataURL(file);
  };

  const downloadFile = async (f) => {
    try {
      const r = await api.get(`/employee/files/${f.id}/download`);
      const a = document.createElement("a");
      a.href = r.data.content_base64; a.download = r.data.filename;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Errore download"); }
  };
  const delFile = async (id) => {
    if (!window.confirm("Eliminare questo file?")) return;
    await api.delete(`/employee/files/${id}`); toast.success("Eliminato"); loadFiles();
  };
  const downloadPayslip = async (p) => {
    try {
      const r = await api.get(`/employee/payslips/${p.id}/download`);
      const a = document.createElement("a");
      a.href = r.data.file_base64; a.download = r.data.filename || `cedolino-${p.month}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Errore download cedolino"); }
  };

  const submitLeave = async (e) => {
    e.preventDefault();
    if (!leaveForm.start_date || !leaveForm.end_date) { toast.error("Date obbligatorie"); return; }
    if (leaveForm.end_date < leaveForm.start_date) { toast.error("Data fine precedente a inizio"); return; }
    try {
      const payload = { ...leaveForm, hours: leaveForm.hours ? parseFloat(leaveForm.hours) : null };
      await api.post("/employee/leaves", payload);
      toast.success("Richiesta inviata");
      setLeaveForm({ type: "holiday", start_date: "", end_date: "", hours: "", notes: "" });
      loadLeaves(); loadDashboard();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };
  const cancelLeave = async (id) => {
    if (!window.confirm("Annullare la richiesta?")) return;
    try { await api.delete(`/employee/leaves/${id}`); toast.success("Annullata"); loadLeaves(); loadDashboard(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  // ---- Rimborsi ----
  const onExpenseReceipt = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED.includes(f.type)) { toast.error("Formato non consentito"); return; }
    if (f.size > MAX_MB * 1024 * 1024) { toast.error(`Max ${MAX_MB}MB`); return; }
    const r = new FileReader();
    r.onload = () => setExpForm({ ...expForm, receipt_base64: r.result });
    r.readAsDataURL(f);
  };
  const submitExpense = async (e) => {
    e.preventDefault();
    if (!expForm.title.trim() || !expForm.amount) { toast.error("Descrizione e importo obbligatori"); return; }
    setExpSubmitting(true);
    try {
      await api.post("/employee/expenses", {
        title: expForm.title,
        amount: Number(expForm.amount),
        currency: "EUR",
        category: expForm.category,
        date: new Date(expForm.date).toISOString(),
        notes: expForm.notes,
        receipt_base64: expForm.receipt_base64 || null,
      });
      toast.success("Nota spese inviata");
      setExpForm({ title: "", amount: "", category: "Trasporti", date: new Date().toISOString().slice(0,10), notes: "", receipt_base64: "" });
      loadExpenses(); loadDashboard();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setExpSubmitting(false); }
  };
  const cancelExpense = async (id) => {
    if (!window.confirm("Eliminare questa nota spese?")) return;
    try { await api.delete(`/employee/expenses/${id}`); toast.success("Eliminata"); loadExpenses(); loadDashboard(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };
  const viewReceipt = async (id) => {
    try {
      const r = await api.get(`/employee/expenses/${id}/receipt`);
      if (!r.data.receipt_base64) { toast.info("Nessuno scontrino allegato"); return; }
      const w = window.open("", "_blank");
      if (w) w.document.write(`<img src="${r.data.receipt_base64}" style="max-width:100%"/>`);
    } catch { toast.error("Errore"); }
  };

  const handleLogout = () => { logout(); nav("/login"); };

  const tabs = [
    { id: "dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { id: "checkin", icon: QrCode, label: "Timbratura" },
    { id: "calendar", icon: Calendar, label: "Calendario" },
    { id: "chat", icon: MessageCircle, label: "Chat", count: chatUnread },
    { id: "signatures", icon: FileSignature, label: "Da firmare", count: signRequests.filter(r=>r.status==="pending").length },
    { id: "files", icon: FileText, label: "I miei file", count: files.length },
    { id: "payslips", icon: Receipt, label: "Cedolini", count: payslips.length },
    { id: "expenses", icon: Wallet, label: "Rimborsi", count: expenses.filter(e=>e.status==="pending").length },
    { id: "leaves", icon: CalendarRange, label: "Ferie/Permessi", count: leaves.filter(l=>l.status==="pending").length },
  ];

  return (
    <div className="min-h-screen bg-[#FFFAF5]" data-testid="employee-portal">
      <header className="bg-[#0A0A0A] text-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3"><LogoMark size={40} /><div className="text-lg font-display">Portale Dipendente</div></Link>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={()=>setAvatarOpen(true)}
              data-testid="portal-avatar-btn"
              title="Cambia avatar"
              className="ring-2 ring-white/30 hover:ring-[#FF7A29] rounded-full transition-all"
            >
              <Avatar avatar={myAvatar} name={user?.full_name?.split(" ")[0]} lastname={user?.full_name?.split(" ").slice(1).join(" ")} size={40} testId="portal-avatar"/>
            </button>
            <div className="text-right hidden md:block">
              <div className="text-xs uppercase tracking-widest text-white/50">Ciao</div>
              <div className="text-sm font-semibold" data-testid="employee-name">{user?.full_name}</div>
            </div>
            <Button variant="ghost" onClick={handleLogout} data-testid="portal-logout" className="text-white/80 hover:text-white hover:bg-white/10 rounded-full">
              <LogOut className="w-4 h-4 mr-2"/> Esci
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        {profile && (
          <div className="card-smart p-6 mb-6 grid md:grid-cols-3 gap-6">
            <div className="flex items-center gap-3">
              <Avatar avatar={myAvatar} name={profile.employee?.name} lastname={profile.employee?.lastname} size={48} testId="portal-card-avatar"/>
              <div>
                <div className="text-xs uppercase tracking-widest text-[#757575]">Dipendente</div>
                <div className="font-semibold">{profile.employee?.name} {profile.employee?.lastname}</div>
                <div className="text-xs text-[#757575]">{profile.employee?.job || "—"}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#FFF0E3] flex items-center justify-center"><Building2 className="w-7 h-7 text-[#FF7A29]"/></div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[#757575]">Azienda</div>
                <div className="font-semibold">{profile.company?.name}</div>
                <div className="text-xs text-[#757575]">{profile.company?.vat_number || "—"}</div>
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-[#757575]">Codice Fiscale</div>
              <div className="font-mono text-sm">{profile.employee?.fiscal_code}</div>
              <div className="text-xs text-[#757575] mt-2">Email portale</div>
              <div className="text-sm">{user?.email}</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={()=>setTab(t.id)} data-testid={`portal-tab-${t.id}`}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold flex items-center gap-2 transition-all ${active ? "bg-[#FF7A29] text-white shadow-lg shadow-orange-500/30" : "bg-white border border-[#EDE5D8] hover:border-[#FF7A29]"}`}>
                <Icon className="w-4 h-4"/> {t.label}
                {t.count > 0 && <span className={`text-[10px] px-1.5 rounded-full ${active ? "bg-white/30" : "bg-[#FFF0E3] text-[#E85D00]"}`}>{t.count}</span>}
              </button>
            );
          })}
        </div>

        {tab === "dashboard" && dashboard && (
          <div data-testid="portal-dashboard" className="space-y-6">
            <TrasfertaWidget />
            {/* Saldo ferie + hero */}
            <div className="grid md:grid-cols-4 gap-4">
              <div className="card-smart p-6 md:col-span-2 bg-gradient-to-br from-[#FF7A29] to-[#E85D00] text-white border-0">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest opacity-80">Saldo ferie {dashboard.year}</div>
                    <div className="font-display text-5xl mt-1" data-testid="dash-holiday-remaining">{dashboard.holiday.remaining}</div>
                    <div className="text-xs opacity-80 mt-1">giorni rimanenti su {dashboard.holiday.allowance}</div>
                  </div>
                  <Sparkles className="w-6 h-6 opacity-70"/>
                </div>
                <div className="mt-4 h-2 bg-white/25 rounded-full overflow-hidden">
                  <div className="h-full bg-white rounded-full transition-all" style={{width: `${Math.min(100, (dashboard.holiday.used / Math.max(1, dashboard.holiday.allowance)) * 100)}%`}}/>
                </div>
                <div className="flex justify-between text-[10px] opacity-80 mt-2">
                  <span>Usati: {dashboard.holiday.used}g</span>
                  <span>Totali: {dashboard.holiday.allowance}g</span>
                </div>
              </div>

              <div className="card-smart p-6">
                <div className="flex items-start justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-widest text-[#757575]">Permessi {dashboard.year}</div>
                  <Clock className="w-4 h-4 text-[#FF7A29]"/>
                </div>
                <div className="font-display text-4xl" data-testid="dash-permission-hours">{dashboard.permission_hours_used.toFixed(1)}</div>
                <div className="text-xs text-[#757575]">ore usate</div>
              </div>

              <div className="card-smart p-6">
                <div className="flex items-start justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-widest text-[#757575]">Malattia</div>
                  <UserCircle className="w-4 h-4 text-[#FF7A29]"/>
                </div>
                <div className="font-display text-4xl" data-testid="dash-illness-days">{dashboard.illness_days}</div>
                <div className="text-xs text-[#757575]">giorni {dashboard.year}</div>
              </div>
            </div>

            {/* Presenze del mese */}
            <div className="grid md:grid-cols-4 gap-4">
              <div className="card-smart p-5 bg-[#0A0A0A] text-white border-[#0A0A0A]">
                <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1">Ingressi mese</div>
                <div className="font-display text-3xl" data-testid="dash-checkins">{dashboard.attendances_month.checkins}</div>
              </div>
              <div className="card-smart p-5">
                <div className="text-[10px] uppercase tracking-widest text-[#757575] mb-1">Uscite mese</div>
                <div className="font-display text-3xl" data-testid="dash-checkouts">{dashboard.attendances_month.checkouts}</div>
              </div>
              <div className="card-smart p-5">
                <div className="text-[10px] uppercase tracking-widest text-[#757575] mb-1">Richieste aperte</div>
                <div className="font-display text-3xl" data-testid="dash-leaves-pending">{dashboard.leaves_counts.pending}</div>
              </div>
              <div className="card-smart p-5">
                <div className="text-[10px] uppercase tracking-widest text-[#757575] mb-1">Approvate {dashboard.year}</div>
                <div className="font-display text-3xl">{dashboard.leaves_counts.approved}</div>
              </div>
            </div>

            {/* Riepilogo dettagliato ferie & permessi per tipologia (anno corrente) */}
            {dashboard.by_type && (
              <div className="card-smart overflow-hidden" data-testid="dash-by-type">
                <div className="px-6 py-4 border-b border-[#EDE5D8] flex items-center justify-between flex-wrap gap-2">
                  <h3 className="font-display text-lg">Riepilogo permessi {dashboard.year}</h3>
                  <span className="text-[10px] uppercase tracking-widest text-[#757575]">Solo richieste APPROVATE</span>
                </div>
                {/* Barra ferie annuali (saldo CCNL) */}
                {dashboard.holiday && dashboard.holiday.allowance > 0 && (
                  <div className="px-6 py-5 bg-gradient-to-r from-[#FFF0E3] to-white border-b border-[#EDE5D8]" data-testid="dash-holiday-progress">
                    <div className="flex items-end justify-between mb-2 flex-wrap gap-2">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-[#757575] font-bold">Saldo ferie annuale (CCNL)</div>
                        <div className="font-display text-2xl mt-1">
                          <span data-testid="dash-holiday-used">{dashboard.holiday.used}</span>
                          <span className="text-base text-[#757575]"> / {dashboard.holiday.allowance} giorni usati</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-widest text-[#757575]">Residui</div>
                        <div className="font-display text-3xl text-[#FF7A29]" data-testid="dash-holiday-remaining">{dashboard.holiday.remaining}</div>
                      </div>
                    </div>
                    <div className="h-3 bg-[#EDE5D8] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#FF7A29] to-[#E85D00] rounded-full transition-all duration-700"
                        style={{ width: `${Math.min(100, (dashboard.holiday.used / dashboard.holiday.allowance) * 100)}%` }}
                        data-testid="dash-holiday-bar"
                      />
                    </div>
                    <div className="flex justify-between text-[10px] text-[#757575] mt-1.5 font-semibold">
                      <span>0</span>
                      <span>{Math.round((dashboard.holiday.used / dashboard.holiday.allowance) * 100)}% consumato</span>
                      <span>{dashboard.holiday.allowance}</span>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 divide-x divide-y divide-[#EDE5D8]">
                  {[
                    { key: "holiday", label: "Ferie", unit: "gg", useDays: true },
                    { key: "permission", label: "Permesso", unit: "h", useDays: false },
                    { key: "rol", label: "ROL", unit: "h", useDays: false },
                    { key: "law_104", label: "Legge 104", unit: "gg", useDays: true },
                    { key: "parental_leave", label: "Congedo parentale", unit: "gg", useDays: true },
                    { key: "medical_visit", label: "Visita medica", unit: "h", useDays: false },
                    { key: "union", label: "Sindacale", unit: "h", useDays: false },
                    { key: "illness", label: "Malattia", unit: "gg", useDays: true },
                  ].map((t) => {
                    const bt = dashboard.by_type[t.key] || { days: 0, hours: 0, approved_count: 0, pending_count: 0 };
                    const value = t.useDays ? bt.days : (bt.hours || 0).toFixed(1);
                    const nothing = (bt.approved_count || 0) === 0 && (bt.pending_count || 0) === 0;
                    return (
                      <div key={t.key} data-testid={`by-type-${t.key}`} className={`px-5 py-4 ${nothing ? "opacity-50" : ""}`}>
                        <div className="text-[10px] uppercase tracking-widest text-[#757575] mb-1">{t.label}</div>
                        <div className="font-display text-2xl">
                          {value}<span className="text-sm ml-1 text-[#757575]">{t.unit}</span>
                        </div>
                        <div className="text-[10px] text-[#757575] mt-1">
                          {bt.approved_count} approv. · {bt.pending_count} in attesa
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* /Riepilogo per tipologia */}

            {/* Rimborsi */}
            {dashboard.expenses && (
              <div className="grid md:grid-cols-3 gap-4">
                <div className="card-smart p-5">
                  <div className="flex items-start justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-widest text-[#757575]">Rimborsi in attesa</div>
                    <Wallet className="w-4 h-4 text-[#FF7A29]"/>
                  </div>
                  <div className="font-display text-3xl" data-testid="dash-exp-pending">{dashboard.expenses.pending}</div>
                  <div className="text-xs text-[#757575]">€{dashboard.expenses.pending_amount.toFixed(2)}</div>
                </div>
                <div className="card-smart p-5">
                  <div className="text-[10px] uppercase tracking-widest text-[#757575] mb-1">Rimborsi approvati {dashboard.year}</div>
                  <div className="font-display text-3xl" data-testid="dash-exp-approved">{dashboard.expenses.approved}</div>
                  <div className="text-xs text-[#757575]">€{dashboard.expenses.approved_amount.toFixed(2)}</div>
                </div>
                <div className="card-smart p-5 bg-gradient-to-br from-[#0A0A0A] to-[#222] text-white border-0">
                  <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1">Totale erogato {dashboard.year}</div>
                  <div className="font-display text-4xl">€{dashboard.expenses.approved_amount.toFixed(2)}</div>
                </div>
              </div>
            )}

            {/* Attività recente */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="card-smart overflow-hidden">
                <div className="px-6 py-4 border-b border-[#EDE5D8] flex items-center justify-between">
                  <h3 className="font-display text-lg">Ultime timbrature</h3>
                  <span className="text-[10px] uppercase tracking-widest text-[#757575]">Mese {dashboard.month}</span>
                </div>
                {dashboard.recent_attendances.length === 0 ? (
                  <div className="p-8 text-center text-sm text-[#757575]">Nessuna timbratura questo mese.</div>
                ) : (
                  <ul className="divide-y divide-[#EDE5D8]">
                    {dashboard.recent_attendances.map((a, idx) => (
                      <li key={idx} className="px-6 py-3 text-sm">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2 flex-wrap min-w-0">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-widest ${a.kind === "in" ? "bg-green-100 text-green-700" : a.kind === "out" ? "bg-amber-100 text-amber-700" : "bg-orange-100 text-orange-700"}`}>{({in:"Ingresso", out:"Uscita", lunch_start:"Pausa pranzo", lunch_end:"Fine pausa"})[a.kind] || a.kind}</span>
                            <span className="font-semibold truncate">{a.qr_label}</span>
                            <WorkModeBadge mode={a.work_mode} size="xs"/>
                            {!a.valid && <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Fuori zona</span>}
                          </div>
                          <span className="text-xs text-[#757575]">{new Date(a.timestamp).toLocaleString("it-IT")}</span>
                        </div>
                        {a.note && <div className="text-xs text-[#444] italic mt-1 line-clamp-2" title={a.note}>“{a.note}”</div>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="card-smart overflow-hidden">
                <div className="px-6 py-4 border-b border-[#EDE5D8]">
                  <h3 className="font-display text-lg">Ultime richieste</h3>
                </div>
                {dashboard.recent_leaves.length === 0 ? (
                  <div className="p-8 text-center text-sm text-[#757575]">Nessuna richiesta.</div>
                ) : (
                  <ul className="divide-y divide-[#EDE5D8]">
                    {dashboard.recent_leaves.map((l) => (
                      <li key={l.id} className="px-6 py-3 text-sm">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{typeLabel(l.type)}</span>
                            {statusBadge(l.status)}
                          </div>
                          <span className="text-xs text-[#757575]">{l.start_date} → {l.end_date}</span>
                        </div>
                        {l.decision_note && <div className="text-[10px] italic text-[#757575] mt-1">Risposta: {l.decision_note}</div>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === "checkin" && (
          <EmployeeCheckinTab/>
        )}

        {tab === "chat" && (
          <div className="card-smart overflow-hidden" style={{ height: "calc(100dvh - 220px)", minHeight: 480 }} data-testid="portal-chat-tab">
            <ChatModule embedded/>
          </div>
        )}

        {tab === "calendar" && (
          <div data-testid="portal-calendar-tab">
            <AttendanceCalendar embedded/>
          </div>
        )}

        {tab === "signatures" && (
          <div data-testid="portal-signatures-tab">
            <div className="card-smart p-6 mb-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#FFF0E3] flex items-center justify-center flex-shrink-0"><FileSignature className="w-6 h-6 text-[#FF7A29]"/></div>
                <div>
                  <h2 className="font-display text-2xl">Documenti da firmare</h2>
                  <p className="text-sm text-[#757575] mt-1">Firma con il dito o con un pennino soft-touch direttamente dal tuo smartphone. Hash crittografico SHA-256 a garanzia di integrità.</p>
                </div>
              </div>
            </div>

            {signRequests.length === 0 ? (
              <div className="card-smart p-10 text-center text-[#757575] text-sm">Nessun documento in attesa di firma.</div>
            ) : (
              <div className="space-y-3">
                {signRequests.map((r) => (
                  <div key={r.id} className="card-smart p-4 md:p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`sign-req-${r.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {r.status === "signed" ? (
                          <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest flex items-center gap-1"><CheckCircle2 className="w-3 h-3"/> Firmato</span>
                        ) : r.status === "revoked" ? (
                          <span className="bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest">Revocato</span>
                        ) : (
                          <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest">In attesa</span>
                        )}
                      </div>
                      <div className="font-display text-lg leading-tight truncate">{r.document_title}</div>
                      <div className="text-xs text-[#757575] mt-1">Richiesta da {r.requested_by_name || "—"} · {new Date(r.created_at).toLocaleDateString("it-IT")}</div>
                      {r.note && <div className="text-xs text-[#444] mt-2 bg-[#FFF8F1] border-l-2 border-[#FF7A29] pl-2 py-1 rounded-r">{r.note}</div>}
                    </div>
                    <div className="flex-shrink-0">
                      {r.status === "pending" ? (
                        <Button onClick={()=>nav(`/sign/${r.id}`)} className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00] btn-glow w-full sm:w-auto" data-testid={`sign-now-${r.id}`}>
                          <FileSignature className="w-4 h-4 mr-2"/> Firma ora
                        </Button>
                      ) : r.status === "signed" ? (
                        <Button variant="ghost" onClick={()=>nav(`/sign/${r.id}`)} className="rounded-full text-[#0A0A0A] hover:bg-[#FFF0E3]">
                          <Download className="w-4 h-4 mr-2"/> Apri
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "files" && (
          <>
            <div className="card-smart p-6 mb-6">
              <div className="mb-4"><h2 className="font-display text-2xl">Carica documento</h2><p className="text-sm text-[#757575] mt-1">Foto (JPG, PNG, WEBP) o PDF · max 10MB</p></div>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label>Categoria</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger data-testid="portal-file-category" className="rounded-xl mt-1"><SelectValue/></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2"><Label>Note (opzionale)</Label><Input className="rounded-xl mt-1" value={notes} onChange={(e)=>setNotes(e.target.value)} placeholder="Descrizione breve…"/></div>
              </div>
              <label className="mt-4 block">
                <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf,image/*,application/pdf" onChange={onUpload} disabled={uploading} className="hidden" data-testid="portal-file-input"/>
                <span className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold cursor-pointer transition-all ${uploading ? "bg-[#757575] text-white" : "bg-[#FF7A29] hover:bg-[#E85D00] text-white btn-glow"}`}>
                  <Upload className="w-4 h-4"/> {uploading ? "Caricamento…" : "Seleziona file"}
                </span>
              </label>
            </div>

            <div className="card-smart overflow-hidden" data-testid="portal-files-list">
              <div className="px-6 py-4 border-b border-[#EDE5D8] flex items-center justify-between">
                <h3 className="font-display text-xl">I miei file</h3>
                <span className="text-xs text-[#757575]">{files.length} file</span>
              </div>
              {files.length === 0 ? (
                <div className="p-12 text-center text-[#757575]"><FileText className="w-10 h-10 mx-auto mb-3 text-[#FF7A29]/50"/>Nessun file ancora caricato.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="border-b border-[#EDE5D8]"><tr className="text-left"><th className="p-4">Nome file</th><th className="p-4">Categoria</th><th className="p-4">Dim.</th><th className="p-4">Caricato</th><th className="p-4"></th></tr></thead>
                  <tbody>
                    {files.map((f) => (
                      <tr key={f.id} className="border-b border-[#EDE5D8] hover:bg-[#FFF0E3]/50" data-testid={`portal-file-${f.id}`}>
                        <td className="p-4"><div className="flex items-center gap-2">{fileIcon(f.mime_type)}<div><div className="font-semibold">{f.filename}</div>{f.notes && <div className="text-xs text-[#757575]">{f.notes}</div>}</div></div></td>
                        <td className="p-4 text-xs"><span className="bg-[#FFF0E3] text-[#E85D00] px-2 py-1 rounded-full font-semibold uppercase">{CATEGORIES.find((c)=>c.value===f.category)?.label || f.category}</span></td>
                        <td className="p-4 text-xs text-[#757575]">{prettySize(f.size * 0.75)}</td>
                        <td className="p-4 text-xs text-[#757575]">{new Date(f.uploaded_at).toLocaleString("it-IT")}</td>
                        <td className="p-4 text-right">
                          <Button size="icon" variant="ghost" onClick={()=>downloadFile(f)} data-testid={`portal-dl-${f.id}`} className="hover:bg-[#FFF0E3] rounded-full h-8 w-8 mr-1"><Download className="w-3.5 h-3.5"/></Button>
                          <Button size="icon" variant="ghost" onClick={()=>delFile(f.id)} data-testid={`portal-del-${f.id}`} className="hover:bg-red-50 hover:text-red-600 rounded-full h-8 w-8"><Trash2 className="w-3.5 h-3.5"/></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {tab === "payslips" && (
          <div className="card-smart overflow-hidden" data-testid="portal-payslips-list">
            <div className="px-6 py-4 border-b border-[#EDE5D8]"><h3 className="font-display text-xl">I miei cedolini</h3><p className="text-xs text-[#757575] mt-1">Caricati dall'amministrazione. Scaricabili in PDF.</p></div>
            {payslips.length === 0 ? (
              <div className="p-12 text-center text-[#757575]"><Receipt className="w-10 h-10 mx-auto mb-3 text-[#FF7A29]/50"/>Nessun cedolino ancora disponibile.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="border-b border-[#EDE5D8]"><tr className="text-left"><th className="p-4">Mese</th><th className="p-4">File</th><th className="p-4">Caricato il</th><th className="p-4"></th></tr></thead>
                <tbody>
                  {payslips.map((p) => (
                    <tr key={p.id} className="border-b border-[#EDE5D8] hover:bg-[#FFF0E3]/50" data-testid={`portal-payslip-${p.id}`}>
                      <td className="p-4 font-display text-lg">{p.month}</td>
                      <td className="p-4"><div className="flex items-center gap-2"><FileText className="w-4 h-4 text-[#FF7A29]"/>{p.filename}</div></td>
                      <td className="p-4 text-xs text-[#757575]">{new Date(p.created_at).toLocaleDateString("it-IT")}</td>
                      <td className="p-4 text-right">
                        <Button size="sm" onClick={()=>downloadPayslip(p)} data-testid={`portal-payslip-dl-${p.id}`} className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00]"><Download className="w-3.5 h-3.5 mr-1"/> Scarica</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === "expenses" && (
          <>
            <form onSubmit={submitExpense} className="card-smart p-6 mb-6" data-testid="portal-expense-form">
              <div className="mb-4"><h2 className="font-display text-2xl">Nuova nota spese</h2><p className="text-sm text-[#757575] mt-1">Carica lo scontrino e dettagli della spesa · l'amministrazione valuterà il rimborso</p></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                <div className="sm:col-span-2 md:col-span-2"><Label>Descrizione *</Label><Input data-testid="expense-title" className="rounded-xl mt-1" value={expForm.title} onChange={(e)=>setExpForm({...expForm, title: e.target.value})} placeholder="Es. Cena cliente Roma"/></div>
                <div><Label>Importo (€) *</Label><Input type="number" step="0.01" required data-testid="expense-amount" className="rounded-xl mt-1" value={expForm.amount} onChange={(e)=>setExpForm({...expForm, amount: e.target.value})}/></div>
                <div>
                  <Label>Categoria</Label>
                  <Select value={expForm.category} onValueChange={(v)=>setExpForm({...expForm, category: v})}>
                    <SelectTrigger data-testid="expense-category" className="rounded-xl mt-1"><SelectValue/></SelectTrigger>
                    <SelectContent>{EXPENSE_CATEGORIES.map((c)=>(<SelectItem key={c} value={c}>{c}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div><Label>Data</Label><Input type="date" data-testid="expense-date" className="rounded-xl mt-1" value={expForm.date} onChange={(e)=>setExpForm({...expForm, date: e.target.value})}/></div>
                <div className="sm:col-span-2 md:col-span-1"><Label>Note</Label><Input className="rounded-xl mt-1" value={expForm.notes} onChange={(e)=>setExpForm({...expForm, notes: e.target.value})} placeholder="Dettagli opzionali"/></div>
              </div>
              <div className="mt-4 grid md:grid-cols-3 gap-4 items-end">
                <div className="md:col-span-2">
                  <Label>Scontrino (foto o PDF)</Label>
                  <Input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf,image/*,application/pdf" data-testid="expense-receipt" onChange={onExpenseReceipt} className="rounded-xl mt-1"/>
                  {expForm.receipt_base64?.startsWith("data:image/") && <img src={expForm.receipt_base64} alt="preview" className="mt-2 max-h-32 border border-[#EDE5D8] rounded-xl"/>}
                  {expForm.receipt_base64?.startsWith("data:application/pdf") && <div className="mt-2 text-xs text-[#757575] flex items-center gap-1"><FileText className="w-4 h-4 text-[#FF7A29]"/> Scontrino PDF allegato</div>}
                </div>
                <Button type="submit" disabled={expSubmitting} data-testid="expense-submit" className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00] btn-glow h-11">
                  <Plus className="w-4 h-4 mr-2"/> {expSubmitting ? "Invio…" : "Invia richiesta"}
                </Button>
              </div>
            </form>

            <div className="card-smart overflow-hidden" data-testid="portal-expenses-list">
              <div className="px-6 py-4 border-b border-[#EDE5D8] flex items-center justify-between">
                <h3 className="font-display text-xl">Le mie note spese</h3>
                <span className="text-xs text-[#757575]">{expenses.length} totali · {expenses.filter(e=>e.status==="pending").length} in attesa</span>
              </div>
              {expenses.length === 0 ? (
                <div className="p-12 text-center text-[#757575]"><Wallet className="w-10 h-10 mx-auto mb-3 text-[#FF7A29]/50"/>Nessuna nota spese.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="border-b border-[#EDE5D8]"><tr className="text-left"><th className="p-4">Data</th><th className="p-4">Descrizione</th><th className="p-4">Categoria</th><th className="p-4">Importo</th><th className="p-4">Stato</th><th className="p-4"></th></tr></thead>
                  <tbody>
                    {expenses.map((x) => (
                      <tr key={x.id} className="border-b border-[#EDE5D8] hover:bg-[#FFF0E3]/50" data-testid={`portal-expense-${x.id}`}>
                        <td className="p-4 text-xs text-[#757575]">{x.date?.slice(0,10)}</td>
                        <td className="p-4">
                          <div className="font-semibold">{x.title}</div>
                          {x.notes && <div className="text-xs text-[#757575]">{x.notes}</div>}
                          {x.decision_note && <div className="text-[10px] italic text-[#757575] mt-1">Risposta: {x.decision_note}</div>}
                        </td>
                        <td className="p-4 text-xs"><span className="bg-[#FFF0E3] text-[#E85D00] px-2 py-1 rounded-full font-semibold">{x.category}</span></td>
                        <td className="p-4 font-display text-base">€{x.amount?.toFixed(2)}</td>
                        <td className="p-4">{statusBadge(x.status)}</td>
                        <td className="p-4 text-right">
                          <Button size="icon" variant="ghost" onClick={()=>viewReceipt(x.id)} data-testid={`expense-view-${x.id}`} title="Vedi scontrino" className="hover:bg-[#FFF0E3] rounded-full h-7 w-7 mr-1"><ImageIcon className="w-3.5 h-3.5"/></Button>
                          {x.status === "pending" && <Button size="icon" variant="ghost" onClick={()=>cancelExpense(x.id)} data-testid={`expense-cancel-${x.id}`} title="Elimina" className="hover:bg-red-50 hover:text-red-600 rounded-full h-7 w-7"><Trash2 className="w-3.5 h-3.5"/></Button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {tab === "leaves" && (
          <>
            <form onSubmit={submitLeave} className="card-smart p-6 mb-6" data-testid="portal-leave-form">
              <div className="mb-4"><h2 className="font-display text-2xl">Richiesta ferie/permesso</h2><p className="text-sm text-[#757575] mt-1">La tua richiesta sarà valutata dall'amministrazione.</p></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <div>
                  <Label>Tipo</Label>
                  <Select value={leaveForm.type} onValueChange={(v)=>setLeaveForm({...leaveForm, type: v})}>
                    <SelectTrigger data-testid="leave-type" className="rounded-xl mt-1"><SelectValue/></SelectTrigger>
                    <SelectContent>{LEAVE_TYPES.map((t)=>(<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div><Label>Dal</Label><Input type="date" required data-testid="leave-start" className="rounded-xl mt-1" value={leaveForm.start_date} onChange={(e)=>setLeaveForm({...leaveForm, start_date: e.target.value})}/></div>
                <div><Label>Al</Label><Input type="date" required data-testid="leave-end" className="rounded-xl mt-1" value={leaveForm.end_date} onChange={(e)=>setLeaveForm({...leaveForm, end_date: e.target.value})}/></div>
                <div><Label>Ore (opz.)</Label><Input type="number" step="0.5" className="rounded-xl mt-1" value={leaveForm.hours} onChange={(e)=>setLeaveForm({...leaveForm, hours: e.target.value})}/></div>
                <div className="sm:col-span-2 lg:col-span-4"><Label>Note</Label><Input className="rounded-xl mt-1" value={leaveForm.notes} onChange={(e)=>setLeaveForm({...leaveForm, notes: e.target.value})} placeholder="Motivazione, destinazione, ecc."/></div>
              </div>
              <Button type="submit" data-testid="leave-submit" className="mt-4 rounded-full bg-[#FF7A29] hover:bg-[#E85D00] btn-glow"><Plus className="w-4 h-4 mr-2"/> Invia richiesta</Button>
            </form>

            <div className="card-smart overflow-hidden" data-testid="portal-leaves-list">
              <div className="px-6 py-4 border-b border-[#EDE5D8]"><h3 className="font-display text-xl">Le mie richieste</h3></div>
              {leaves.length === 0 ? (
                <div className="p-12 text-center text-[#757575]"><CalendarRange className="w-10 h-10 mx-auto mb-3 text-[#FF7A29]/50"/>Nessuna richiesta.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="border-b border-[#EDE5D8]"><tr className="text-left"><th className="p-4">Tipo</th><th className="p-4">Periodo</th><th className="p-4">Ore</th><th className="p-4">Stato</th><th className="p-4">Note</th><th className="p-4"></th></tr></thead>
                  <tbody>
                    {leaves.map((l) => (
                      <tr key={l.id} className="border-b border-[#EDE5D8]" data-testid={`portal-leave-${l.id}`}>
                        <td className="p-4 font-semibold">{typeLabel(l.type)}</td>
                        <td className="p-4 text-xs text-[#757575]">{l.start_date} → {l.end_date}</td>
                        <td className="p-4 text-xs">{l.hours ?? "—"}</td>
                        <td className="p-4">{statusBadge(l.status)}</td>
                        <td className="p-4 text-xs text-[#757575]">
                          {l.notes && <div>{l.notes}</div>}
                          {l.decision_note && <div className="text-[10px] italic">Risposta: {l.decision_note}</div>}
                        </td>
                        <td className="p-4 text-right">
                          {l.status === "pending" && <Button size="icon" variant="ghost" onClick={()=>cancelLeave(l.id)} data-testid={`leave-cancel-${l.id}`} className="hover:bg-red-50 hover:text-red-600 rounded-full h-7 w-7"><Trash2 className="w-3.5 h-3.5"/></Button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </main>

      <AvatarPicker
        open={avatarOpen}
        onOpenChange={setAvatarOpen}
        value={myAvatar}
        name={user?.full_name?.split(" ")[0] || ""}
        lastname={user?.full_name?.split(" ").slice(1).join(" ") || ""}
        onSelect={async (newAv) => {
          try {
            await api.patch("/me/avatar", { avatar: newAv });
            setMyAvatar(newAv);
            toast.success("Avatar aggiornato");
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
          }
        }}
      />
    </div>
  );
}
